/**
 * =============================================================================
 * Helper: playbookScript
 * Versão: 2.0.0
 *
 * Descrição:
 *   Utilitários para o sistema de Roteiro (script) dos steps de Playbook
 *   de Vendas. Renderiza variáveis dinâmicas ({{contact.first_name}}, etc.)
 *   e sanitiza HTML para exibição segura ao vendedor.
 *
 *   Em v2.0.0 o catálogo foi expandido com grupos: Contato, Negócio,
 *   Produtos, Contratos, Financeiro/Links, Vendedor, Empresa, Tempo —
 *   permitindo enriquecer o script com dados reais do deal e links
 *   acionáveis (cobrança, contrato para assinatura, PIX copia-e-cola).
 *
 * Release notes:
 *   v1.0.0 - Criação (Playbook v2.0 - WhatsApp/Visita + Roteiro)
 *   v2.0.0 - Novos grupos: Produtos, Contratos, Financeiro, Links, Tempo+
 *            Listas (products.list), formatação BRL/data automática,
 *            PREVIEW_CONTEXT cobrindo todos os tokens.
 * =============================================================================
 */
import DOMPurify from 'dompurify';

// ----------------------------------------------------------------------------
// Tipos do contexto de renderização — todos opcionais para resiliência
// ----------------------------------------------------------------------------

/** Item de produto associado ao deal (usado em products.list) */
export interface ScriptProductItem {
  name: string;
  quantity?: number | null;
  unit_price?: number | null;
  total?: number | null;
}

export interface ScriptRenderContext {
  contact?: {
    first_name?: string | null;
    full_name?: string | null;
    phone?: string | null;
    /** Telefone em formato E.164 só com dígitos (sem +) */
    phone_e164?: string | null;
    /** Link wa.me já pronto para abrir conversa */
    whatsapp_link?: string | null;
    email?: string | null;
    /** CPF ou CNPJ do contato/cliente */
    document?: string | null;
    city?: string | null;
    state?: string | null;
  };
  deal?: {
    title?: string | null;
    value?: number | string | null;
    phase?: string | null;
    pipeline?: string | null;
    /** Identificador curto do negócio (ex.: número sequencial) */
    code?: string | null;
    created_at?: string | null;
    expected_close_date?: string | null;
    source?: string | null;
    /** Tags do negócio (lista) */
    tags?: string[] | null;
  };
  products?: ScriptProductItem[] | null;
  contract?: {
    title?: string | null;
    status?: string | null;
    number?: string | number | null;
    /** Link do provider (D4Sign/Autentique/etc.) para o cliente assinar */
    signature_link?: string | null;
    /** URL pública do PDF gerado */
    pdf_link?: string | null;
    sent_at?: string | null;
    signed_at?: string | null;
  };
  charge?: {
    value?: number | null;
    due_date?: string | null;
    status?: string | null;
    /** Link de pagamento do provider (Asaas invoiceUrl, etc.) */
    payment_link?: string | null;
    boleto_link?: string | null;
    pix_copia_cola?: string | null;
    pix_qr_url?: string | null;
  };
  installments?: {
    count?: number | null;
    next_due_date?: string | null;
    next_value?: number | null;
    overdue_count?: number | null;
    overdue_total?: number | null;
  };
  seller?: {
    first_name?: string | null;
    full_name?: string | null;
    email?: string | null;
    phone?: string | null;
  };
  tenant?: {
    name?: string | null;
    phone?: string | null;
    support_email?: string | null;
  };
}

// ----------------------------------------------------------------------------
// Catálogo de variáveis — usado pela UI (VariablePicker) e pela validação
// ----------------------------------------------------------------------------

/** Identificador dos grupos exibidos na UI (abas do VariablePicker) */
export type VariableGroup =
  | 'Contato'
  | 'Negócio'
  | 'Produtos'
  | 'Contrato'
  | 'Financeiro'
  | 'Vendedor'
  | 'Empresa'
  | 'Tempo';

export interface AvailableVariable {
  token: string;
  label: string;
  group: VariableGroup;
  /** Texto descritivo (tooltip) com exemplo de valor renderizado */
  example?: string;
  /** Marca como recém-adicionada (badge "Novo") */
  isNew?: boolean;
}

export const AVAILABLE_VARIABLES: AvailableVariable[] = [
  // ---- Contato ----
  { token: 'contact.first_name', label: 'Primeiro nome', group: 'Contato', example: 'João' },
  { token: 'contact.full_name', label: 'Nome completo', group: 'Contato', example: 'João da Silva' },
  { token: 'contact.phone', label: 'Telefone', group: 'Contato', example: '+55 11 98765-4321' },
  { token: 'contact.phone_e164', label: 'Telefone (E.164)', group: 'Contato', example: '5511987654321', isNew: true },
  { token: 'contact.whatsapp_link', label: 'Link WhatsApp', group: 'Contato', example: 'https://wa.me/5511987654321', isNew: true },
  { token: 'contact.email', label: 'E-mail', group: 'Contato', example: 'joao@exemplo.com' },
  { token: 'contact.document', label: 'CPF / CNPJ', group: 'Contato', example: '123.456.789-00', isNew: true },
  { token: 'contact.city', label: 'Cidade', group: 'Contato', example: 'São Paulo', isNew: true },
  { token: 'contact.state', label: 'Estado (UF)', group: 'Contato', example: 'SP', isNew: true },

  // ---- Negócio ----
  { token: 'deal.title', label: 'Título do negócio', group: 'Negócio', example: 'Implantação CRM - Acme' },
  { token: 'deal.value', label: 'Valor do negócio', group: 'Negócio', example: 'R$ 15.000,00' },
  { token: 'deal.phase', label: 'Fase atual', group: 'Negócio', example: 'Proposta enviada' },
  { token: 'deal.pipeline', label: 'Pipeline', group: 'Negócio', example: 'Vendas B2B' },
  { token: 'deal.code', label: 'Número do negócio', group: 'Negócio', example: '#1024', isNew: true },
  { token: 'deal.created_at', label: 'Criado em', group: 'Negócio', example: '01/05/2026', isNew: true },
  { token: 'deal.expected_close_date', label: 'Previsão de fechamento', group: 'Negócio', example: '30/05/2026', isNew: true },
  { token: 'deal.source', label: 'Origem', group: 'Negócio', example: 'WhatsApp', isNew: true },
  { token: 'deal.tags', label: 'Tags', group: 'Negócio', example: 'quente, vip', isNew: true },

  // ---- Produtos ----
  { token: 'products.list', label: 'Lista de produtos (bullets)', group: 'Produtos', example: '- Plano Pro × 1 — R$ 199,00', isNew: true },
  { token: 'products.list_compact', label: 'Lista compacta (uma linha)', group: 'Produtos', example: 'Plano Pro × 1, Setup × 1', isNew: true },
  { token: 'products.count', label: 'Total de itens', group: 'Produtos', example: '2', isNew: true },
  { token: 'products.total_value', label: 'Soma dos produtos', group: 'Produtos', example: 'R$ 699,00', isNew: true },
  { token: 'products.first_name', label: 'Nome do primeiro produto', group: 'Produtos', example: 'Plano Pro', isNew: true },

  // ---- Contrato ----
  { token: 'contract.title', label: 'Título do contrato', group: 'Contrato', example: 'Contrato de Prestação', isNew: true },
  { token: 'contract.status', label: 'Status', group: 'Contrato', example: 'Aguardando assinatura', isNew: true },
  { token: 'contract.number', label: 'Número', group: 'Contrato', example: '#42', isNew: true },
  { token: 'contract.signature_link', label: 'Link para assinar', group: 'Contrato', example: 'https://app.d4sign.com.br/...', isNew: true },
  { token: 'contract.pdf_link', label: 'Link do PDF', group: 'Contrato', example: 'https://...contrato.pdf', isNew: true },
  { token: 'contract.sent_at', label: 'Enviado em', group: 'Contrato', example: '10/05/2026', isNew: true },
  { token: 'contract.signed_at', label: 'Assinado em', group: 'Contrato', example: '12/05/2026', isNew: true },

  // ---- Financeiro ----
  { token: 'charge.value', label: 'Valor da cobrança', group: 'Financeiro', example: 'R$ 199,00', isNew: true },
  { token: 'charge.due_date', label: 'Vencimento', group: 'Financeiro', example: '20/05/2026', isNew: true },
  { token: 'charge.status', label: 'Status', group: 'Financeiro', example: 'Pendente', isNew: true },
  { token: 'charge.payment_link', label: 'Link de pagamento', group: 'Financeiro', example: 'https://www.asaas.com/i/abc123', isNew: true },
  { token: 'charge.boleto_link', label: 'Link do boleto', group: 'Financeiro', example: 'https://...boleto.pdf', isNew: true },
  { token: 'charge.pix_copia_cola', label: 'PIX copia-e-cola', group: 'Financeiro', example: '00020126...', isNew: true },
  { token: 'charge.pix_qr_url', label: 'PIX QR Code (URL)', group: 'Financeiro', example: 'https://...qr.png', isNew: true },
  { token: 'installments.count', label: 'Qtd. de parcelas', group: 'Financeiro', example: '12', isNew: true },
  { token: 'installments.next_due_date', label: 'Próxima parcela (data)', group: 'Financeiro', example: '20/06/2026', isNew: true },
  { token: 'installments.next_value', label: 'Próxima parcela (valor)', group: 'Financeiro', example: 'R$ 199,00', isNew: true },
  { token: 'installments.overdue_count', label: 'Parcelas vencidas', group: 'Financeiro', example: '0', isNew: true },
  { token: 'installments.overdue_total', label: 'Total vencido', group: 'Financeiro', example: 'R$ 0,00', isNew: true },

  // ---- Vendedor ----
  { token: 'seller.first_name', label: 'Primeiro nome', group: 'Vendedor', example: 'Maria' },
  { token: 'seller.full_name', label: 'Nome completo', group: 'Vendedor', example: 'Maria Souza' },
  { token: 'seller.email', label: 'E-mail', group: 'Vendedor', example: 'maria@empresa.com', isNew: true },
  { token: 'seller.phone', label: 'Telefone', group: 'Vendedor', example: '+55 11 90000-0000', isNew: true },

  // ---- Empresa ----
  { token: 'tenant.name', label: 'Nome da empresa', group: 'Empresa', example: 'Sua Empresa' },
  { token: 'tenant.phone', label: 'Telefone da empresa', group: 'Empresa', example: '+55 11 3000-0000', isNew: true },
  { token: 'tenant.support_email', label: 'E-mail de suporte', group: 'Empresa', example: 'suporte@empresa.com', isNew: true },

  // ---- Tempo ----
  { token: 'today', label: 'Data atual', group: 'Tempo', example: new Date().toLocaleDateString('pt-BR') },
  { token: 'now', label: 'Data e hora atuais', group: 'Tempo', example: new Date().toLocaleString('pt-BR') },
  { token: 'tomorrow', label: 'Amanhã', group: 'Tempo', example: '—', isNew: true },
  { token: 'weekday', label: 'Dia da semana', group: 'Tempo', example: 'segunda-feira', isNew: true },
];

/** Ordem fixa dos grupos exibidos nas abas do picker */
export const VARIABLE_GROUP_ORDER: VariableGroup[] = [
  'Contato',
  'Negócio',
  'Produtos',
  'Contrato',
  'Financeiro',
  'Vendedor',
  'Empresa',
  'Tempo',
];

const VAR_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)?)\s*\}\}/g;

// ----------------------------------------------------------------------------
// Utilitários internos
// ----------------------------------------------------------------------------

/** Escapa HTML para evitar XSS quando o valor da variável contém < > & */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Formata valor numérico (BRL) */
function formatBRL(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

/** Formata data ISO/yyyy-mm-dd para dd/mm/aaaa */
function formatDate(v: string | null | undefined): string {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('pt-BR');
}

/** Resolve um token único contra o contexto. Retorna string vazia se ausente. */
function resolveToken(token: string, ctx: ScriptRenderContext): string {
  const today = new Date();

  // Tokens "raiz" sem grupo
  if (token === 'today') return today.toLocaleDateString('pt-BR');
  if (token === 'now') return today.toLocaleString('pt-BR');
  if (token === 'tomorrow') {
    const t = new Date(today);
    t.setDate(t.getDate() + 1);
    return t.toLocaleDateString('pt-BR');
  }
  if (token === 'weekday') {
    return today.toLocaleDateString('pt-BR', { weekday: 'long' });
  }

  // Tokens compostos — group.field
  const [group, field] = token.split('.');

  // ---- Produtos (lista) ----
  if (group === 'products') {
    const list = ctx.products ?? [];
    switch (field) {
      case 'count':
        return String(list.length);
      case 'first_name':
        return list[0]?.name ?? '';
      case 'total_value': {
        const sum = list.reduce((acc, p) => acc + (Number(p.total ?? 0) || 0), 0);
        return formatBRL(sum);
      }
      case 'list_compact':
        return list.map((p) => `${p.name}${p.quantity ? ` × ${p.quantity}` : ''}`).join(', ');
      case 'list':
        return list
          .map(
            (p) =>
              `- ${p.name}${p.quantity ? ` × ${p.quantity}` : ''}${
                p.total != null ? ` — ${formatBRL(p.total)}` : ''
              }`,
          )
          .join('\n');
      default:
        return '';
    }
  }

  // Demais grupos — leitura direta
  const node = (ctx as Record<string, unknown>)[group] as Record<string, unknown> | undefined;
  if (!node) return '';
  const value = node[field];
  if (value === null || value === undefined || value === '') return '';

  // Formatações automáticas por nome do campo
  if (token === 'deal.value' || token === 'charge.value' || token === 'installments.next_value' || token === 'installments.overdue_total') {
    return typeof value === 'number' ? formatBRL(value) : String(value);
  }
  if (
    token === 'deal.created_at' ||
    token === 'deal.expected_close_date' ||
    token === 'contract.sent_at' ||
    token === 'contract.signed_at' ||
    token === 'charge.due_date' ||
    token === 'installments.next_due_date'
  ) {
    return formatDate(String(value));
  }
  if (token === 'deal.tags' && Array.isArray(value)) {
    return value.join(', ');
  }

  return String(value);
}

// ----------------------------------------------------------------------------
// API pública
// ----------------------------------------------------------------------------

/**
 * Renderiza o script substituindo variáveis e sanitizando o HTML.
 * Retorna sempre string segura para `dangerouslySetInnerHTML`.
 */
export function renderScript(html: string | null | undefined, ctx: ScriptRenderContext = {}): string {
  if (!html) return '';
  // 1. Substitui tokens (escapando o valor; quebras de linha viram <br/>)
  const replaced = html.replace(VAR_RE, (_match, token) => {
    const raw = resolveToken(token, ctx);
    return escapeHtml(raw).replace(/\n/g, '<br/>');
  });
  // 2. Sanitiza HTML resultante
  return DOMPurify.sanitize(replaced, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'a', 'ul', 'ol', 'li',
      'blockquote', 'h1', 'h2', 'h3', 'h4', 'code', 'pre', 'span', 'div',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  });
}

/**
 * Renderiza o script como TEXTO PURO (sem HTML) — útil para WhatsApp/SMS/email body.
 */
export function renderScriptAsText(html: string | null | undefined, ctx: ScriptRenderContext = {}): string {
  if (!html) return '';
  const replaced = html.replace(VAR_RE, (_match, token) => resolveToken(token, ctx));
  // Strip de tags via DOM
  const tmp = document.createElement('div');
  tmp.innerHTML = DOMPurify.sanitize(replaced);
  // Substitui <br> e fim de bloco por \n para preservar quebras
  tmp.querySelectorAll('br').forEach((el) => el.replaceWith('\n'));
  tmp.querySelectorAll('p, div, li, h1, h2, h3, h4, blockquote').forEach((el) => {
    el.append('\n');
  });
  return (tmp.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Extrai a lista de variáveis usadas no HTML (para preview e validação).
 */
export function extractVariables(html: string | null | undefined): string[] {
  if (!html) return [];
  const set = new Set<string>();
  for (const m of html.matchAll(VAR_RE)) set.add(m[1]);
  return Array.from(set);
}

// ----------------------------------------------------------------------------
// Contexto fictício usado no preview do editor de playbook
// ----------------------------------------------------------------------------
export const PREVIEW_CONTEXT: ScriptRenderContext = {
  contact: {
    first_name: 'João',
    full_name: 'João da Silva',
    phone: '+55 11 98765-4321',
    phone_e164: '5511987654321',
    whatsapp_link: 'https://wa.me/5511987654321',
    email: 'joao.silva@example.com',
    document: '123.456.789-00',
    city: 'São Paulo',
    state: 'SP',
  },
  deal: {
    title: 'Implantação CRM - Acme',
    value: 15000,
    phase: 'Proposta enviada',
    pipeline: 'Vendas B2B',
    code: '#1024',
    created_at: new Date().toISOString(),
    expected_close_date: new Date(Date.now() + 15 * 86400000).toISOString(),
    source: 'WhatsApp',
    tags: ['quente', 'vip'],
  },
  products: [
    { name: 'Plano Pro', quantity: 1, unit_price: 199, total: 199 },
    { name: 'Setup', quantity: 1, unit_price: 500, total: 500 },
  ],
  contract: {
    title: 'Contrato de Prestação',
    status: 'Aguardando assinatura',
    number: '#42',
    signature_link: 'https://app.d4sign.com.br/exemplo',
    pdf_link: 'https://exemplo.com/contrato.pdf',
    sent_at: new Date().toISOString(),
    signed_at: null,
  },
  charge: {
    value: 199,
    due_date: new Date(Date.now() + 5 * 86400000).toISOString(),
    status: 'Pendente',
    payment_link: 'https://www.asaas.com/i/abc123',
    boleto_link: 'https://exemplo.com/boleto.pdf',
    pix_copia_cola: '00020126580014br.gov.bcb.pix...',
    pix_qr_url: 'https://exemplo.com/qr.png',
  },
  installments: {
    count: 12,
    next_due_date: new Date(Date.now() + 30 * 86400000).toISOString(),
    next_value: 199,
    overdue_count: 0,
    overdue_total: 0,
  },
  seller: {
    first_name: 'Maria',
    full_name: 'Maria Souza',
    email: 'maria@empresa.com',
    phone: '+55 11 90000-0000',
  },
  tenant: {
    name: 'Sua Empresa',
    phone: '+55 11 3000-0000',
    support_email: 'suporte@empresa.com',
  },
};
